(() => {
  if (customElements.get('hh-pdp-capsule-motion')) return;
  class HhPdpCapsuleMotion extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.video = this.querySelector('video');
      this.button = this.querySelector('button');
      if (!this.video || !this.button) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.manualVisibilityTime = -Infinity;
      this.ready = false;
      this.pending = false;
      this.blocked = false;
      this.userPaused = false;
      this.ended = false;
      this.video.controls = false;
      this.button.hidden = false;
      this.video.muted = true;
      this.video.loop = this.video.dataset.loop !== 'false';
      this.posterImage = document.createElement('img');
      this.posterImage.src = this.video.poster;
      this.posterImage.alt = '';
      this.posterImage.setAttribute('aria-hidden', 'true');
      this.posterImage.dataset.hhCapsulePoster = '';
      Object.assign(this.posterImage.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none', zIndex: '1' });
      this.button.style.zIndex = '2';
      this.insertBefore(this.posterImage, this.button);
      this.button.addEventListener('click', () => {
        if (this.pending || !this.video.paused) {
          this.userPaused = true;
          this.video.pause();
        } else {
          this.userPaused = false;
          this.blocked = false;
          if (this.ended) { this.video.currentTime = 0; this.ended = false; }
          // A browser may scroll this control into view before delivering the
          // observer callback. Measure the click's current viewport now so a
          // legitimate first gesture is not paused by stale visibility state.
          this.manualVisibilityTime = performance.now();
          const bounds = this.video.getBoundingClientRect();
          this.visible = bounds.width > 0 && bounds.height > 0 &&
            bounds.bottom > 0 && bounds.right > 0 &&
            bounds.top < window.innerHeight && bounds.left < window.innerWidth;
          this.load();
          this.play();
        }
      }, options);
      this.video.addEventListener('play', () => this.update(), options);
      this.video.addEventListener('pause', () => { this.cancelFrame(); this.update(); }, options);
      this.video.addEventListener('playing', () => this.revealFrame(), options);
      this.video.addEventListener('ended', () => { this.ended = true; this.update(); }, options);
      this.video.addEventListener('canplay', () => this.auto(), options);
      this.video.addEventListener('error', () => { this.cancelFrame(); this.posterImage.hidden = false; this.blocked = true; this.pending = false; this.video.controls = true; this.button.hidden = true; }, options);
      document.addEventListener('visibilitychange', () => this.auto(), options);
      this.motion.addEventListener('change', () => { this.video.pause(); this.auto(); }, options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          // Already-queued observations can describe the position before
          // the browser scrolled to the clicked control. Later observations
          // still own clipping/offscreen pause; only stale ones are ignored.
          if (entries[0].time < this.manualVisibilityTime) return;
          this.visible = entries[0].isIntersecting;
          this.auto();
        }, { threshold: 0.2 });
        this.observer.observe(this.video);
      } else this.visible = true;
      const schedule = () => {
        const allow = () => { if (this.isConnected) { this.ready = true; this.auto(); } };
        if ('requestIdleCallback' in window) this.idle = requestIdleCallback(allow, { timeout: 1500 });
        else this.timer = setTimeout(allow, 500);
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true, signal: this.abort.signal });
      else schedule();
    }
    load() {
      if (this.video.hasAttribute('src')) return;
      this.video.src = this.video.dataset.src;
      this.video.load();
    }
    cancelFrame() {
      if (this.frame !== undefined) this.video.cancelVideoFrameCallback?.(this.frame);
      if (this.paint !== undefined) cancelAnimationFrame(this.paint);
      this.frame = undefined;
      this.paint = undefined;
    }
    revealFrame() {
      this.cancelFrame();
      if (this.posterImage.hidden) return;
      const connection = this.abort;
      const reveal = () => {
        if (this.abort !== connection || !this.isConnected || this.video.paused || document.hidden || !this.visible) return;
        this.posterImage.hidden = true;
      };
      if (this.video.requestVideoFrameCallback) this.frame = this.video.requestVideoFrameCallback(reveal);
      else {
        const check = () => {
          if (this.abort !== connection || !this.isConnected || this.video.paused || document.hidden || !this.visible) return;
          if (this.video.readyState >= 2 && this.video.currentTime > 0) reveal();
          else this.paint = requestAnimationFrame(check);
        };
        this.paint = requestAnimationFrame(check);
      }
    }
    auto() {
      if (!this.visible || document.hidden) { this.video.pause(); return; }
      if (!this.observer || this.motion.matches || !this.ready || this.userPaused || this.blocked || this.ended || navigator.connection?.saveData) return;
      this.load();
      if (this.video.readyState >= 3) this.play();
    }
    play() {
      if (this.pending || !this.video.paused) return;
      this.pending = true;
      const connection = this.abort;
      const video = this.video;
      this.video.play().then(() => {
        if (this.abort !== connection || !this.isConnected) return;
        if (this.userPaused || document.hidden || !this.visible) video.pause();
      }).catch(() => {
        if (this.abort === connection && this.isConnected) this.blocked = true;
      }).finally(() => {
        if (this.abort !== connection || !this.isConnected) return;
        this.pending = false;
        this.update();
      });
    }
    update() {
      const state = this.ended ? 'replay' : this.video.paused ? 'play' : 'pause';
      const text = this.button.dataset[state];
      if (this.button.getAttribute('aria-label') !== text) this.button.setAttribute('aria-label', text);
      if (this.button.dataset.state !== state) this.button.dataset.state = state;
    }
    disconnectedCallback() {
      this.cancelFrame();
      this.posterImage?.remove();
      this.abort?.abort();
      this.abort = null;
      this.observer?.disconnect();
      if (this.idle !== undefined) cancelIdleCallback(this.idle);
      clearTimeout(this.timer);
      this.video?.pause();
      this.video?.removeAttribute('src');
      this.video?.load();
    }
  }
  customElements.define('hh-pdp-capsule-motion', HhPdpCapsuleMotion);
})();

